import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { safeAnchorHref } from '../../../lib/link';
import { cn } from '../../../lib/utils';
import { SpecraButton } from '../ensigns/button';
import { specraFocusRing } from '../ensigns/focus-ring';
import { SpecraSwatch } from '../ensigns/swatch';
import { SpecraText } from '../ensigns/typography';
// SAD-1000: the frame every Specra chart sits in. A chart is a figure with a
// title, a one-line description, an optional control (a view switch), the
// plot, a legend, and a table of the same numbers one click away, so no
// value is reachable only by colour or by hovering.
// FND-01: the tone vocabulary moved down to ensigns/chart-tone; re-exported
// here so existing lieutenant imports keep working.
export { chartToneVar } from '../ensigns/chart-tone';
/**
 * The horizontal gutters of a plot: room for the y-axis labels on the left
 * and an end label on the right. A trend and the column strip under it share
 * them, so their columns line up.
 */ export const chartPlotGutters = 'pl-[var(--space-8)] pr-[var(--space-12)]';
/** A chart's legend: the dependable identity channel beside the colour. */ export function SpecraChartLegend({ items, className, children }) {
    return /*#__PURE__*/ _jsxs("ul", {
        "aria-label": "Legend",
        className: cn('flex flex-wrap items-center gap-x-[var(--space-3)] gap-y-[var(--space-1)]', className),
        children: [
            children,
            items.map((item)=>/*#__PURE__*/ _jsxs("li", {
                    className: "flex items-center gap-[var(--space-1-5)]",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSwatch, {
                            shape: item.shape === undefined || item.shape === 'rect' ? 'square' : item.shape,
                            tone: item.tone
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            size: "meta",
                            tone: "muted",
                            children: item.labelContent ?? item.label
                        }),
                        item.action
                    ]
                }, item.label))
        ]
    });
}
/** A titled chart figure with a legend and a table view of its numbers. */ export function SpecraChartFrame({ title, description, actions, insight, legend, table, children, className, ...props }) {
    const [asTable, setAsTable] = React.useState(false);
    const titleId = React.useId();
    return /*#__PURE__*/ _jsxs("figure", {
        "aria-labelledby": titleId,
        className: cn('m-0 flex min-w-0 flex-col gap-[var(--space-3)] rounded-[var(--radius-lg)] border border-[color:var(--border-card)] bg-[var(--surface-card)] p-[var(--space-4)]', className),
        ...props,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-start justify-between gap-[var(--space-2)] sm:flex-nowrap",
                children: [
                    /*#__PURE__*/ _jsxs("figcaption", {
                        className: "min-w-0 flex-1",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                as: "div",
                                className: "font-medium",
                                id: titleId,
                                size: "body-sm",
                                children: title
                            }),
                            description ? /*#__PURE__*/ _jsx(SpecraText, {
                                as: "div",
                                size: "meta",
                                tone: "muted",
                                children: description
                            }) : null
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex shrink-0 items-center gap-[var(--space-1)]",
                        children: [
                            actions,
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "aria-pressed": asTable,
                                "data-chart-table-toggle": "",
                                onClick: ()=>setAsTable((v)=>!v),
                                size: "xs",
                                variant: "ghost",
                                children: asTable ? 'Chart' : 'Table'
                            })
                        ]
                    })
                ]
            }),
            insight && !asTable ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "body-ui",
                tone: "secondary",
                children: insight
            }) : null,
            asTable ? /*#__PURE__*/ _jsx(ChartTable, {
                caption: title,
                table: table
            }) : children,
            legend && !asTable ? legend : null
        ]
    });
}
function ChartTable({ caption, table }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "max-h-[var(--component-chart-table-max-height)] overflow-auto",
        children: /*#__PURE__*/ _jsxs("table", {
            className: "w-full border-collapse text-[length:var(--fs-ui-body)]",
            "data-chart-table": "",
            children: [
                /*#__PURE__*/ _jsx("caption", {
                    className: "sr-only",
                    children: caption
                }),
                /*#__PURE__*/ _jsx("thead", {
                    children: /*#__PURE__*/ _jsx("tr", {
                        children: table.columns.map((c)=>/*#__PURE__*/ _jsx("th", {
                                className: cn('border-b border-border py-[var(--space-1)] pr-[var(--space-2)] text-[length:var(--fs-ui-eyebrow)] font-medium uppercase tracking-[var(--ls-ui-group)] text-muted-foreground', c.numeric ? 'text-right' : 'text-left'),
                                scope: "col",
                                children: c.label
                            }, c.key))
                    })
                }),
                /*#__PURE__*/ _jsx("tbody", {
                    children: table.rows.map((r)=>/*#__PURE__*/ _jsx("tr", {
                            children: table.columns.map((c)=>/*#__PURE__*/ _jsx("td", {
                                    className: cn('border-b border-[color:var(--border-subtle)] py-[var(--space-1)] pr-[var(--space-2)]', c.numeric && 'text-right tabular-nums'),
                                    children: r.cells[c.key]
                                }, c.key))
                        }, r.id))
                })
            ]
        })
    });
}
/**
 * A chart mark that drills: a router link when the host gives a renderer,
 * else a plain anchor; with no href, an inert span. Forwards its ref so a
 * tooltip can wrap it.
 */ export const SpecraChartLink = /*#__PURE__*/ React.forwardRef(({ href, renderLink, className, children, ...props }, ref)=>{
    const classes = cn(specraFocusRing, 'block', className);
    if (href && renderLink) {
        return renderLink({
            ...props,
            children,
            className: classes,
            href,
            ref: ref
        });
    }
    const safe = href ? safeAnchorHref(href) : undefined;
    if (safe) {
        return /*#__PURE__*/ _jsx("a", {
            className: classes,
            href: safe,
            ref: ref,
            ...props,
            children: children
        });
    }
    return /*#__PURE__*/ _jsx("span", {
        className: cn('block', className),
        ref: ref,
        ...props,
        children: children
    });
});
SpecraChartLink.displayName = 'SpecraChartLink';
